/**
 * Hover intent for the header's top-level menu.
 *
 * Stock `<header-menu>` opens on `pointerenter` and closes on `pointerleave`
 * with no delay, so a pointer crossing the nav on its way somewhere else flicks
 * the Shop panel open and shut. Both handlers are reached through the theme's
 * declarative `on:` bindings, which resolve the method off the element at
 * dispatch time (`assets/component.js`) — so wrapping the two own properties on
 * the instance is enough. The component's own internals keep calling the
 * originals, and no stock file is edited.
 *
 * Only pointer events are delayed. Click, Escape, focus and blur stay instant.
 * Also opens a parent item on keyboard focus (see the `focusin` listener).
 */

/** Pointer must dwell this long on a top-level item before the panel opens. */
const OPEN_DELAY_MS = 80;

/** Grace period after the pointer leaves, so a diagonal move can be recovered. */
const CLOSE_DELAY_MS = 150;

/**
 * @param {Element} menu
 */
function addHoverIntent(menu) {
  if (!(menu instanceof HTMLElement)) return;
  if (menu.dataset.bettinoHoverIntent === 'true') return;

  const instance = /** @type {any} */ (menu);

  // `activate` is a class field (already bound); `deactivate` is a prototype
  // method, so it needs binding before the instance property shadows it.
  const activate = instance.activate;
  const deactivate = instance.deactivate;
  if (typeof activate !== 'function' || typeof deactivate !== 'function') return;

  const activateNow = activate.bind(instance);
  const deactivateNow = deactivate.bind(instance);

  menu.dataset.bettinoHoverIntent = 'true';

  // Keyboard: stock opens on pointer and on the disclosure button only, so tabbing
  // onto a parent link shows nothing until the next Tab. Open on the link's own
  // keyboard focus. Not the disclosure button: Escape returns focus there, and
  // reopening on it would undo the close. `:focus-visible` keeps a mouse click
  // (which also focuses the link) from doubling up with hover.
  menu.addEventListener('focusin', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (!target.matches('.menu-list__link:focus-visible')) return;
    if (!target.closest('.menu-list__list-item')?.querySelector('.menu-list__submenu')) return;
    instance.activate(event);
  });

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let openTimer;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let closeTimer;

  /** @param {(event: Event) => void} run @param {Event} event */
  const later = (run, event, delay) =>
    setTimeout(() => {
      if (!menu.isConnected) return;
      run(event);
    }, delay);

  /** @param {Event} event */
  instance.activate = (event) => {
    clearTimeout(closeTimer);
    closeTimer = undefined;
    clearTimeout(openTimer);
    openTimer = undefined;

    if (event?.type !== 'pointerenter') {
      activateNow(event);
      return;
    }

    openTimer = later(activateNow, event, OPEN_DELAY_MS);
  };

  /** @param {Event} event */
  instance.deactivate = (event) => {
    // A pointer that leaves before the dwell is met never opened anything.
    clearTimeout(openTimer);
    openTimer = undefined;

    if (event?.type !== 'pointerleave') {
      deactivateNow(event);
      return;
    }

    clearTimeout(closeTimer);
    closeTimer = later(deactivateNow, event, CLOSE_DELAY_MS);
  };
}

function enhanceAll() {
  for (const menu of document.querySelectorAll('header-menu')) {
    customElements.upgrade(menu);
    addHoverIntent(menu);
  }
}

customElements.whenDefined('header-menu').then(() => {
  enhanceAll();
  // The customizer re-renders the header group through the Section Rendering
  // API, which replaces the element and its unwrapped handlers.
  document.addEventListener('shopify:section:load', enhanceAll);
});
